import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Skeleton, Chip, ToggleButtonGroup, ToggleButton, LinearProgress } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AppDispatch, RootState } from '../../store/store';
import { getInsights } from '../../rtk-apis/dashboard/getInsights';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Medical Receptionist dashboard — front-desk workflow insights.
   Distinct from reader/scanner views: chase overdue reports, track reminders,
   watch distribution failures. MUI shell + KPI cards + Highcharts.
   Data from GET /v2/dashboard/insights (role-aware — Receptionist payload).
   ════════════════════════════════════════════════════════════════════════ */

const fmtShortDate = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${+m[3]} ${mon}`;
};

const daysSince = (d?: string | number) => {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

const fmtRelative = (d?: string | number) => {
  const days = daysSince(d);
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  const dt = new Date(d as any);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()];
  return `${dt.getDate()} ${mon}`;
};

const chartBase: Highcharts.Options = {
  credits: { enabled: false },
  title: { text: undefined },
  chart: { backgroundColor: 'transparent', style: { fontFamily: "'Hanken Grotesk', sans-serif" } },
  legend: { itemStyle: { color: T.ink2, fontWeight: '600' }, itemHoverStyle: { color: T.ink } },
};

const ReceptionistDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { result, loading } = useSelector((s: RootState) => s.getInsightsResponse);
  const data = result?.data;

  const [overdueSort, setOverdueSort] = useState<'longest' | 'recent'>('longest');

  useEffect(() => {
    dispatch(getInsights({ overdueSort }));
  }, [dispatch, overdueSort]);

  // Skeletons only on the first load; the sort toggle keeps data on screen
  // and shows a thin progress bar on its own panel instead.
  const initialLoading = loading && !data;
  const refreshing = loading && !!data;

  const k = data?.kpis;
  const statusB = data?.statusBreakdown || { Draft: 0, Preliminary: 0, Finalized: 0 };
  const trend = data?.trend || [];
  const weekdayVolume = data?.weekdayVolume || [];
  const distributionStatus = data?.distributionStatus || [];
  const overdueQueue = data?.overdueQueue || [];
  const failedDistributions = data?.failedDistributions || [];

  // ── charts ─────────────────────────────────────────────────────────────
  const trendOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'areaspline', height: 280 },
    xAxis: {
      categories: trend.map((d: any) => fmtShortDate(d.date)),
      labels: { style: { color: T.ink3, fontSize: '10px' }, step: 4 },
      lineColor: T.line,
      tickColor: T.line,
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: 'rgba(255,255,255,0.05)',
      labels: { style: { color: T.ink3 } },
      allowDecimals: false,
    },
    tooltip: { shared: true, backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink } },
    plotOptions: { areaspline: { fillOpacity: 0.18, lineWidth: 2, marker: { enabled: false } } },
    series: [
      { type: 'areaspline', name: 'Created', data: trend.map((d: any) => d.created), color: T.sky },
      { type: 'areaspline', name: 'Finalized', data: trend.map((d: any) => d.finalized), color: T.teal },
    ],
  }), [trend]);

  const statusOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'pie', height: 280 },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
    plotOptions: { pie: { innerSize: '62%', borderWidth: 0, dataLabels: { enabled: true, style: { color: T.ink2, textOutline: 'none' }, format: '{point.name}: {point.y}' } } },
    series: [{
      type: 'pie',
      name: 'Reports',
      data: [
        { name: 'Draft', y: statusB.Draft, color: T.amber },
        { name: 'Preliminary', y: statusB.Preliminary, color: T.sky },
        { name: 'Finalized', y: statusB.Finalized, color: T.green },
      ],
    }],
  }), [statusB]);

  const distOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'pie', height: 280 },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b>' },
    plotOptions: { pie: { innerSize: '62%', borderWidth: 0, dataLabels: { enabled: true, style: { color: T.ink2, textOutline: 'none' }, format: '{point.name}: {point.y}' } } },
    series: [{
      type: 'pie',
      name: 'Distributions',
      data: distributionStatus.map((d: any) => ({
        name: prettyDist(d.status),
        y: d.count,
        color: distColor(d.status),
      })),
    }],
  }), [distributionStatus]);

  const weekdayOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'column', height: 280 },
    xAxis: { categories: weekdayVolume.map((d: any) => d.day), labels: { style: { color: T.ink2 } }, lineColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> reports' },
    plotOptions: { column: { borderRadius: 3, color: T.sky, borderWidth: 0 } },
    series: [{ type: 'column', name: 'Reports', data: weekdayVolume.map((d: any) => d.count) }],
  }), [weekdayVolume]);

  const hasDist = distributionStatus.length > 0;

  return (
    <Box sx={{ px: '30px', pb: '30px', pt: '18px', color: T.ink, fontFamily: "'Hanken Grotesk', sans-serif", overflowY: 'auto', height: 'calc(100vh - 64px)' }}>
      {/* header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.teal, opacity: 0.85 }}>
          Front Desk Overview
        </Typography>
        <Typography component="h1" sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.05, background: 'linear-gradient(92deg,#fff,#bfe9f5 55%,#7fe3d4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Dashboard
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3,1fr)', xl: 'repeat(6,1fr)' }, gap: '14px', mb: '18px' }}>
        <Kpi loading={initialLoading} icon={<PendingActionsOutlinedIcon />} accent={T.amber} label="Pending reports" value={k?.pendingReports} />
        <Kpi loading={initialLoading} icon={<ReportProblemOutlinedIcon />} accent={T.red} label="Overdue reports" value={k?.overdueReports} highlight={(k?.overdueReports || 0) > 0} />
        <Kpi loading={initialLoading} icon={<NotificationsActiveOutlinedIcon />} accent={T.violet} label="Reminders sent (mo)" value={k?.remindersSentThisMonth} />
        <Kpi loading={initialLoading} icon={<TaskAltOutlinedIcon />} accent={T.sky} label="Finalized today" value={k?.finalizedToday} />
        <Kpi loading={initialLoading} icon={<AssignmentTurnedInOutlinedIcon />} accent={T.green} label="Finalized this month" value={k?.finalizedThisMonth} />
        <Kpi loading={initialLoading} icon={<SendOutlinedIcon />} accent={T.red} label="Failed distributions" value={k?.distributionsFailed} highlight={(k?.distributionsFailed || 0) > 0} sub={k?.distributionsDelivered != null ? `${k.distributionsDelivered} delivered` : undefined} />
      </Box>

      {/* charts row 1 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="Reports — last 30 days" subtitle="Created vs finalized" loading={initialLoading}>
          <HighchartsReact highcharts={Highcharts} options={trendOptions} />
        </Panel>
        <Panel title="Report status" subtitle="All reports in your hospital" loading={initialLoading}>
          <HighchartsReact highcharts={Highcharts} options={statusOptions} />
        </Panel>
      </Box>

      {/* charts row 2 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="Distribution status" subtitle="Report sends to providers" loading={initialLoading} empty={!hasDist}>
          <HighchartsReact highcharts={Highcharts} options={distOptions} />
        </Panel>
        <Panel title="Weekday volume" subtitle="Reports by weekday" loading={initialLoading}>
          <HighchartsReact highcharts={Highcharts} options={weekdayOptions} />
        </Panel>
      </Box>

      {/* bottom row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px' }}>
        <Panel
          title="Reports to chase"
          subtitle={overdueSort === 'longest' ? 'Longest overdue first' : 'Most recent first'}
          loading={initialLoading}
          refreshing={refreshing}
          empty={!loading && overdueQueue.length === 0}
          emptyText="Nothing overdue — the desk is clear."
          action={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={overdueSort}
              onChange={(_e, v) => v && setOverdueSort(v)}
              sx={{ '& .MuiToggleButton-root': { py: '2px', px: '10px', fontSize: 12, color: T.ink3, borderColor: T.line, '&.Mui-selected': { color: T.teal, background: `${T.teal}1f` } } }}
            >
              <ToggleButton value="longest">Longest overdue</ToggleButton>
              <ToggleButton value="recent">Most recent</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {overdueQueue.map((r: any) => {
              const overdueDays = daysSince(r.createdOn);
              return (
                <Box
                  key={r._id}
                  onClick={() => navigate(`/patientReports/${r._id}`)}
                  sx={{
                    display: 'grid', gridTemplateColumns: '1fr 130px 92px 24px', alignItems: 'center', gap: '12px',
                    px: '12px', py: '11px', borderBottom: `1px solid ${T.line}`, cursor: 'pointer', borderRadius: '8px',
                    '&:hover': { background: T.surface2 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.reportName || r.name || r.patientId || r.uhid || 'Report'}
                      </Typography>
                      {(r.reminderCount || 0) > 0 && (
                        <Chip
                          label={`Reminded ×${r.reminderCount}`}
                          size="small"
                          sx={{ height: 18, fontWeight: 700, fontSize: 10, borderRadius: '999px', color: T.violet, background: `${T.violet}1f`, border: `1px solid ${T.violet}66` }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: T.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.reportType || '—'} · UHID {r.uhid || '—'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: T.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.reportAssignedTo || 'Unassigned'}
                  </Typography>
                  <Box>
                    <Chip
                      label={overdueDays != null ? `${overdueDays}d overdue` : 'Overdue'}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: 11, borderRadius: '999px', color: T.red, background: `${T.red}1f`, border: `1px solid ${T.red}66` }}
                    />
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18, color: T.ink3 }} />
                </Box>
              );
            })}
          </Box>
        </Panel>

        <Panel title="Failed distributions" subtitle="Sends that need re-routing or follow-up" loading={initialLoading} empty={!loading && failedDistributions.length === 0} emptyText="No failed sends — everything delivered.">
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {failedDistributions.map((d: any) => (
              <Box
                key={d._id}
                onClick={() => d.reportId && navigate(`/patientReports/${d.reportId}`)}
                sx={{
                  display: 'grid', gridTemplateColumns: '1fr 140px 80px 24px', alignItems: 'center', gap: '12px',
                  px: '12px', py: '11px', borderBottom: `1px solid ${T.line}`, cursor: d.reportId ? 'pointer' : 'default', borderRadius: '8px',
                  '&:hover': { background: T.surface2 },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.toProviderName || 'Unknown provider'}
                </Typography>
                <Box>
                  <Chip
                    label={prettyDist(d.status)}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11, borderRadius: '999px', color: T.red, background: `${T.red}1f`, border: `1px solid ${T.red}66` }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12.5, color: T.ink3, whiteSpace: 'nowrap' }}>{fmtRelative(d.updatedAt)}</Typography>
                <ChevronRightIcon sx={{ fontSize: 18, color: T.ink3, opacity: d.reportId ? 1 : 0.25 }} />
              </Box>
            ))}
          </Box>
        </Panel>
      </Box>
    </Box>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────
const Kpi: React.FC<{ icon: React.ReactNode; accent: string; label: string; value: any; unit?: string; sub?: string; highlight?: boolean; loading?: boolean }> = ({ icon, accent, label, value, unit, sub, highlight, loading }) => (
  <Box sx={{ minWidth: 0, position: 'relative', borderRadius: '14px', background: 'linear-gradient(180deg, rgba(20,36,57,0.6), rgba(14,28,46,0.55))', border: `1px solid ${highlight ? `${accent}88` : T.line}`, p: '14px 16px', overflow: 'hidden', minHeight: 96 }}>
    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: accent, mb: '6px' }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink3 }}>{label}</Typography>
      <Box sx={{ display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
    </Box>
    {loading ? (
      <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
    ) : (
      <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 30, fontWeight: 600, lineHeight: 1, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? 0}
        {unit && <Box component="span" sx={{ fontSize: 14, color: T.ink3, ml: '3px', fontFamily: "'Hanken Grotesk',sans-serif" }}>{unit}</Box>}
      </Typography>
    )}
    {sub && !loading && <Typography sx={{ fontSize: 11.5, color: T.ink3, mt: '4px' }}>{sub}</Typography>}
  </Box>
);

// ── chart / widget panel ───────────────────────────────────────────────────
const Panel: React.FC<{ title: string; subtitle?: string; loading?: boolean; refreshing?: boolean; empty?: boolean; emptyText?: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, loading, refreshing, empty, emptyText, action, children }) => (
  <Box sx={{ minWidth: 0, overflow: 'hidden', borderRadius: '16px', background: 'rgba(14,28,46,0.55)', border: `1px solid ${T.line}`, p: '16px 18px', position: 'relative' }}>
    {refreshing && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
    <Box sx={{ mb: '8px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
      <Box>
        <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 16, fontWeight: 600, color: T.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: T.ink3 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
    {loading ? (
      <Skeleton variant="rounded" height={240} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
    ) : empty ? (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink3, fontSize: 13 }}>
        {emptyText || 'No data yet.'}
      </Box>
    ) : (
      children
    )}
  </Box>
);

const prettyDist = (s: string) =>
  ({
    DRAFT: 'Draft', SENT: 'Sent', ACCEPTED_BY_MO: 'Accepted (MO)', DELIVERED: 'Delivered',
    FILE_CONSUMED: 'File consumed', PRACTICE_ACK: 'Practice ack', DELIVERY_FAILED: 'Delivery failed',
    PRACTICE_REJECTED: 'Rejected', FAILED: 'Failed', NOT_ROUTABLE: 'Not routable', ACK_ERROR: 'Ack error',
  } as Record<string, string>)[s] || s;

const distColor = (s: string) => {
  if (['DELIVERED', 'FILE_CONSUMED', 'PRACTICE_ACK', 'ACCEPTED_BY_MO'].includes(s)) return T.green;
  if (['DELIVERY_FAILED', 'PRACTICE_REJECTED', 'FAILED', 'NOT_ROUTABLE', 'ACK_ERROR'].includes(s)) return T.red;
  if (s === 'SENT') return T.sky;
  return T.ink3;
};

export default ReceptionistDashboard;
